<template>
  <section class="conversation-history-page">
    <div class="history-container">
      <header class="page-header">
        <div>
          <p class="eyebrow">Conversations</p>
          <h1>Conversation History</h1>
          <p class="subtext">
            Review past conversations between users and your AI agent.
          </p>
        </div>

        <div class="status-badge">
          Last {{ MAX_SESSIONS_TO_LOAD }} sessions
        </div>
      </header>

      <div v-if="loading" class="state-box">
        <div class="loader"></div>
        <h2>Loading conversations...</h2>
        <p>Please wait while we retrieve your agent conversation history.</p>
      </div>

      <div v-else-if="error" class="error-box">
        <h2>Unable to load conversations</h2>
        <p>{{ error }}</p>
      </div>

      <div v-else-if="conversations.length === 0" class="empty-box">
        <h2>No conversations found</h2>
        <p>
          Once users start interacting with your AI agent, their conversations
          will appear here.
        </p>
      </div>

      <div v-else class="conversations">
        <article
          class="conversation-card"
          v-for="conv in conversations"
          :key="conv.session_id"
        >
          <div class="conversation-header">
            <div class="conversation-header-left">
              <strong>{{ conv.displayName }}</strong>

              <div class="session-meta">
                Session #{{ conv.session_id }}
                <span v-if="conv.external_id">• {{ conv.external_id }}</span>
              </div>
            </div>

            <span class="conversation-date">
              {{ formatDate(conv.created_at) }}
            </span>
          </div>

          <div class="conversation-body">
            <div v-if="conv.messages.length === 0" class="empty-message">
              No messages found for this session.
            </div>

            <div
              v-for="msg in conv.messages"
              :key="msg.id"
              class="message"
              :class="msg.sender"
            >
              <div class="message-top">
                <span class="role" :class="`role-${msg.sender}`">
                  {{ msg.roleLabel }}
                </span>

                <span class="message-time">
                  {{ formatDate(msg.timestamp) }}
                </span>
              </div>

              <div class="message-text">
                {{ msg.text }}
              </div>

              <div v-if="msg.tokensUsed !== null" class="tokens">
                Tokens: {{ msg.tokensUsed }}
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Cookies from 'js-cookie'
import { sessions, conversationHistory } from '@/services/api'
import { useRouter } from 'vue-router'

const router = useRouter()

const conversations = ref([])
const loading = ref(true)
const error = ref('')

const agentId = Cookies.get('active_agent_id')
const MAX_SESSIONS_TO_LOAD = 50

function formatDate(dateString) {
  if (!dateString) return '—'

  const d = new Date(dateString)

  if (Number.isNaN(d.getTime())) return '—'

  return d.toLocaleString()
}

function getDisplayName(session) {
  if (session.name && session.name.trim()) return session.name
  if (session.email && session.email.trim()) return session.email
  if (session.phone && session.phone.trim()) return session.phone

  if (session.external_id && String(session.external_id).trim()) {
    return `Visitor (${session.external_id})`
  }

  return 'Unknown visitor'
}

function normalizeMessages(rawMessages) {
  if (!Array.isArray(rawMessages)) return []

  return rawMessages
    .map((msg) => ({
      id: msg.id,
      sender: msg.sender || 'unknown',
      roleLabel:
        msg.sender === 'user'
          ? 'User'
          : msg.sender === 'assistant'
          ? 'Agent'
          : msg.sender === 'system'
          ? 'System'
          : 'Unknown',
      text: msg.message || '',
      timestamp: msg.timestamp || null,
      tokensUsed: msg.tokens_used ?? null
    }))
    .sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0

      return aTime - bTime
    })
}

async function fetchConversations() {
  const token = Cookies.get('token')

  if (!token) {
    router.push('/login')
    return
  }

  if (!agentId) {
    error.value = 'No active agent found. Please select an agent first.'
    loading.value = false
    return
  }

  try {
    const sessionsResponse = await sessions.getAll(agentId)

    const allSessions = Array.isArray(sessionsResponse)
      ? sessionsResponse
      : Array.isArray(sessionsResponse?.data)
      ? sessionsResponse.data
      : []

    const recentSessions = [...allSessions]
      .sort((a, b) => {
        const aTime = a.started_at ? new Date(a.started_at).getTime() : 0
        const bTime = b.started_at ? new Date(b.started_at).getTime() : 0
        return bTime - aTime
      })
      .slice(0, MAX_SESSIONS_TO_LOAD)

    const conversationPromises = recentSessions.map(async (session) => {
      try {
        const historyResponse = await conversationHistory.getBySession(session.id)

        const rawMessages = Array.isArray(historyResponse)
          ? historyResponse
          : Array.isArray(historyResponse?.data)
          ? historyResponse.data
          : []

        return {
          session_id: session.id,
          external_id: session.external_id,
          displayName: getDisplayName(session),
          created_at: session.started_at,
          messages: normalizeMessages(rawMessages)
        }
      } catch (err) {
        console.warn(`Could not load session ${session.id}`, err)

        return {
          session_id: session.id,
          external_id: session.external_id,
          displayName: getDisplayName(session),
          created_at: session.started_at,
          messages: []
        }
      }
    })

    conversations.value = await Promise.all(conversationPromises)
  } catch (err) {
    console.error('Conversation history loading error:', err)
    error.value = 'Unable to load conversation history.'
  } finally {
    loading.value = false
  }
}

onMounted(fetchConversations)
</script>

<style scoped>
.conversation-history-page {
  min-height: 100vh;
  background: #f8fafc;
  padding: 48px 20px;
  color: #0f172a;
}

.history-container {
  max-width: 980px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 28px;
}

.eyebrow {
  color: #6366f1;
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  margin-bottom: 10px;
}

h1 {
  font-size: 2.5rem;
  font-weight: 900;
  letter-spacing: -0.05em;
  color: #020617;
  margin-bottom: 10px;
}

.subtext {
  color: #64748b;
  font-size: 0.98rem;
  line-height: 1.7;
  max-width: 640px;
}

.status-badge {
  background: #020617;
  color: white;
  border-radius: 999px;
  padding: 10px 16px;
  font-size: 0.82rem;
  font-weight: 900;
  white-space: nowrap;
}

.state-box,
.error-box,
.empty-box {
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 24px;
  padding: 34px;
  box-shadow: 0 20px 70px rgba(15, 23, 42, 0.06);
}

.state-box,
.empty-box {
  text-align: center;
}

.state-box h2,
.error-box h2,
.empty-box h2 {
  font-size: 1.4rem;
  font-weight: 900;
  color: #020617;
  margin-bottom: 8px;
}

.state-box p,
.error-box p,
.empty-box p {
  color: #64748b;
  line-height: 1.7;
}

.error-box {
  border-color: rgba(239, 68, 68, 0.22);
  background: #fff;
}

.error-box h2 {
  color: #991b1b;
}

.loader {
  width: 34px;
  height: 34px;
  border: 3px solid rgba(15, 23, 42, 0.12);
  border-top-color: #020617;
  border-radius: 50%;
  margin: 0 auto 18px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.conversations {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.conversation-card {
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 24px;
  padding: 22px;
  box-shadow: 0 18px 60px rgba(15, 23, 42, 0.05);
}

.conversation-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.conversation-header-left {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.conversation-header-left strong {
  font-size: 1.02rem;
  color: #020617;
}

.session-meta {
  font-size: 0.82rem;
  color: #64748b;
}

.conversation-date {
  color: #64748b;
  font-size: 0.86rem;
  white-space: nowrap;
}

.conversation-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-message {
  color: #94a3b8;
  font-style: italic;
  padding: 10px 0;
}

.message {
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
}

.message.user {
  background: #eef2ff;
  border-color: #c7d2fe;
}

.message.assistant {
  background: #f8fafc;
}

.message.system {
  background: #fff7ed;
  border-color: #fed7aa;
}

.message.unknown {
  background: #f9fafb;
}

.message-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  gap: 1rem;
}

.role {
  font-weight: 900;
  font-size: 0.86rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.role-user {
  color: #3730a3;
}

.role-assistant {
  color: #020617;
}

.role-system {
  color: #c2410c;
}

.message-time {
  font-size: 0.78rem;
  color: #64748b;
  white-space: nowrap;
}

.message-text {
  color: #1f2937;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.65;
}

.tokens {
  margin-top: 8px;
  font-size: 0.76rem;
  color: #64748b;
}

@media (max-width: 720px) {
  .conversation-history-page {
    padding: 28px 14px;
  }

  .page-header,
  .conversation-header {
    flex-direction: column;
  }

  h1 {
    font-size: 2rem;
  }

  .conversation-card,
  .state-box,
  .error-box,
  .empty-box {
    border-radius: 20px;
    padding: 20px;
  }

  .message-top {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>